import React from 'react';
import { theme } from 'antd';
import { ChartDataset } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';

import { numberFormatter } from '~library/numbers';
import { getXAxisConfig, getKPIChartDatasetOptions } from './Chart';
import { getKPIComparison } from './components/KPIComparison';
import { type WidgetLoadError } from '~features/empty-states';
import KPICard from './components/KPICard';
import KPICardContent from './components/KPICardContent';
import { useIsPrinting } from '~library/print';

function KPIAverageCrawlTimeContents({ rawData }: { rawData: AggregatedMetrics.DeliveryTimeSummaryResult }) {
  const { token } = theme.useToken();
  const { isPrinting } = useIsPrinting();

  const comparison = React.useMemo(
    () => getKPIComparison(rawData.vCurrent, rawData.vPrevious),
    [rawData.vCurrent, rawData.vPrevious]
  );

  const dataset = React.useMemo(() => {
    const data = rawData.data.reduce<{
      labels: Date[];
      datasets: ChartDataset<'line'>[];
    }>(
      (acc, item) => {
        acc.labels.push(new Date(item.time));
        acc.datasets[0].data.push(+(item.avg / 1000).toFixed(2));
        return acc;
      },
      { labels: [], datasets: [{ data: [] }] }
    );
    let lineColor = token.colorText;
    if (comparison.kind === 'delta') {
      if (comparison.percentage < 0) lineColor = token.colorSuccess;
      else if (comparison.percentage > 0) lineColor = token.colorError;
    }

    Object.assign(data.datasets[0], getKPIChartDatasetOptions(lineColor));

    return data;
  }, [comparison, rawData, token.colorError, token.colorSuccess, token.colorText]);

  const percentageColor =
    comparison.kind === 'delta' && comparison.percentage < 0 ? 'success' : 'danger';

  const originalTime = +(rawData.originalTimeAvg / 1000).toFixed(2);
  return (
    <KPICardContent
      comparison={comparison}
      percentageColor={percentageColor}
      mainValue={`${numberFormatter.format(rawData.vCurrent / 1000 || 0)}s`}
    >
      <Line
        data={dataset}
        options={{
          animation: isPrinting ? false : undefined,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ...getXAxisConfig(rawData.granularity), display: false },
            y: { display: false },
          },
          plugins: {
            annotation: {
              annotations: {
                thresholdLine: {
                  type: 'line',
                  yMin: originalTime,
                  yMax: originalTime,
                  borderColor: '#BFBFBF',
                  borderWidth: 1,
                  borderDash: [5, 5], // optional: dashed line
                  label: {
                    display: true,
                    content: `Original: ${originalTime}s`,
                    position: 'end',
                    backgroundColor: 'transparent',
                    color: '#8C8C8C',
                    z: 1,
                    yAdjust: 5,
                    font: {
                      weight: 'normal',
                      size: 13,
                    },
                  },
                },
              },
            },
            tooltip: {
              enabled: false,
            },
          },
        }}
      />
    </KPICardContent>
  );
}

export const TOOLTIP_MESSAGE =
  'The time it takes for crawlers to load your pages. Longer times can lead to delays in indexing new or updated content.';

export interface KPIAverageCrawlTimeProps {
  isLoading: boolean;
  error?: WidgetLoadError;
  rawData?: AggregatedMetrics.DeliveryTimeSummaryResult;
}

function KPIAverageCrawlTime({ isLoading, rawData, error }: KPIAverageCrawlTimeProps) {
  const link = React.useMemo(() => {
    const urlParameters = new URLSearchParams(window.location.search);
    urlParameters.set('chart_type', 'response-time');
    return `/cdn-analytics?${urlParameters.toString()}`;
  }, [window.location.search]);

  return (
    <KPICard title="Average Crawl Time" titleTooltip={TOOLTIP_MESSAGE} isLoading={isLoading} error={error} link={link}>
      {rawData && <KPIAverageCrawlTimeContents rawData={rawData} />}
    </KPICard>
  );
}

export default KPIAverageCrawlTime;
